<template>
  <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:header-options>
      <div v-if="hasArchiveView" class="sub-filters">
        <button type="button" class="sub-filter-button" :class="{ active: !isArchiveView }" @click="setArchiveView(false)">Non intéressés</button>
        <button type="button" class="sub-filter-button" :class="{ active: isArchiveView }" @click="setArchiveView(true)">Archivés</button>
      </div>
      <button @click="$refs.addClientModal.openModal()" class="action-button" type="button"><span class="fas fa-plus"></span>Nouveau client</button>
    </template>
    <template v-slot:table>
      <thead v-if="$refs.table">
        <tr>
          <custom-header field="createdAt" class="hide-for-mobile col-fit">Date de création</custom-header>
          <custom-header class="col-fit" field="name">Nom</custom-header>
          <custom-header class="col-fit" field="phone">Téléphone</custom-header>
          <custom-header field="mail" class="hide-for-mobile col-fit">Mail</custom-header>
          <custom-header field="contact" class="hide-for-mobile col-fit">Contact</custom-header>
          <custom-header field="lastContact" class="hide-for-mobile col-fit">Dernier contact</custom-header>
          <custom-header field="status" class="hide-for-mobile col-fit">Statut</custom-header>
          <custom-header class="col-fit" field="actions">Actions</custom-header>
        </tr>
      </thead>
      <tbody v-if="data && data.length > 0">
        <tr v-for="(client, index) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="index" class="business-row" @click="$refs.editClientModal.openModal(client)">
          <custom-body field="createdAt" class="hide-for-mobile col-fit">{{(new Date(client.createdAt)).toLocaleDateString("en-GB", formattedDateTime)}}</custom-body>
          <custom-body class="col-fit col-truncate" field="name" :title="client.structuredInformations.name"><div class="name"><span v-if="client.clientId" title="Ce client a accès à ces devis et factures" class="fas fa-circle-info"></span><p>{{client.structuredInformations.name}}</p></div></custom-body>
          <custom-body class="col-fit" field="phone">{{client.structuredInformations.phoneNumber}}</custom-body>
          <custom-body field="mail" class="hide-for-mobile col-fit col-truncate" :title="client.structuredInformations.mail">{{client.structuredInformations.mail}}</custom-body>
          <custom-body field="contact" class="hide-for-mobile col-fit col-truncate" :title="client.contact">{{client.contact}}</custom-body>
          <custom-body field="lastContact" class="hide-for-mobile col-fit">{{client.lastContact ? timeAgo(client.lastContact) : '-'}}</custom-body>
          <custom-body field="status" class="hide-for-mobile col-fit"><StatusBadge :status="client.status" :label="CLIENT_STATUS_ENUM[client.status] ? CLIENT_STATUS_ENUM[client.status].label : undefined" /></custom-body>
          <custom-body class="col-fit" field="actions">
            <button class="button-icon-custom" title="Créer un devis" @click.stop="goToBusiness(client)"><span class="fas fa-file-export"></span></button>
          </custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr style="background-color: #40513b14;font-weight: bold"><td colspan="8" style="text-align: center">{{ isArchiveView ? 'Aucun client archivé' : 'Aucun client enregistré' }}</td></tr></tbody>
    </template>
  </CustomTable>
  <AddClientModal ref="addClientModal" id="add" :callback=loadData />
  <EditClientModal ref="editClientModal" :callback=loadData />
</template>

<script>
    import {ClientController} from "@/controllers/business/ClientController";
    import AddClientModal from "@/components/modals/AddClientModal.vue";
    import EditClientModal from "@/components/modals/EditClientModal.vue";
    import CustomTable from "@/components/CustomTable.vue";
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faMoneyBillTransfer, faList, faChevronLeft, faChevronRight, faFilePen, faTrashCan, faEye, faEyeSlash, faCircleInfo, faFileExport } from "@fortawesome/free-solid-svg-icons";
    import {CLIENT_STATUS_ENUM} from "@/models/clientStatusEnum";
    import {ACTIVATION_STATUS_ENUM} from "@/models/activationStatusEnum";
    import {formattedDateTime, timeAgo} from "@/utils";
    import CustomHeader from "@/components/tables/CustomHeader.vue";
    import CustomBody from "@/components/tables/CustomBody.vue";
    import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
    import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
    import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
    import {CustomTableService} from "@/services/CustomTableService";
    import router from "@/router";
    // Les clients archivés (état DEACTIVATED) sont masqués par défaut ; la rubrique « Non intéressés » permet de les afficher
    const ACTIVE_STATE_FILTER = {field: "state", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [ACTIVATION_STATUS_ENUM.DEACTIVATED.value]};
    const ARCHIVED_STATE_FILTER = {field: "state", operator: FILTER_OPERATOR_ENUM.IN.value, variable: [ACTIVATION_STATUS_ENUM.DEACTIVATED.value]};
    const CLIENT_STATE_VALUES = {
      ACTIVATED: {label: "Actif", value: ACTIVATION_STATUS_ENUM.ACTIVATED.value},
      DEACTIVATED: {label: "Archivé", value: ACTIVATION_STATUS_ENUM.DEACTIVATED.value}
    };
    library.add(faChevronLeft, faFilePen, faList, faMoneyBillTransfer, faChevronRight, faTrashCan, faEye, faEyeSlash, faCircleInfo, faFileExport);

    export default {
      name: 'ClientTable',
      // defaultFilters : filtres de la rubrique affichée (remplacent le filtre par défaut sur le statut) ; loadedCallback : appelé après chaque chargement
      // hasArchiveView : propose de basculer sur les clients archivés
      props: ['defaultFilters', 'loadedCallback', 'hasArchiveView'],
      computed: {
        formattedDateTime() {
          return formattedDateTime
        },
        CLIENT_STATUS_ENUM() {
          return CLIENT_STATUS_ENUM
        }
      },
      components: {
        StatusBadge,
        CustomBody,
        CustomHeader,
        CustomTable,
        EditClientModal,
        AddClientModal
      },
      data: function () {
        return {
          data: [],
          isArchiveView: false,
          elements: [
            {label: "Date de création", value: "createdAt", type: "date"},
            {label: "Nom", value: "name", type: "text"},
            {label: "Description", value: "description", type: "text"},
            {label: "Téléphone", value: "phone", type: "text"},
            {label: "Mail", value: "mail", type: "text"},
            {label: "Contact", value: "contact", type: "text"},
            {label: "Dernier contact", value: "lastContact", type: "date"},
            {label: "Statut", value: "status", type: "select", values: CLIENT_STATUS_ENUM},
            {label: "État", value: "state", type: "select", values: CLIENT_STATE_VALUES},
            {label: "Actions", value: undefined, type: undefined},
          ],
          searchOptions: {
            filters: [{field: "status", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [CLIENT_STATUS_ENUM.UNINTERESTED.value]}, {...ACTIVE_STATE_FILTER}],
            sorts: [{field: "createdAt", order: SORT_OPERATOR_ENUM.DESCENDING.value}]
          }
        }
      },
      beforeMount() {
        // Filtres de la rubrique : ils remplacent les filtres par défaut portant sur le même champ
        if (this.defaultFilters && this.defaultFilters.length > 0) {
          const fields = this.defaultFilters.map(filter => filter.field);
          this.searchOptions = {
            ...this.searchOptions,
            filters: [
              ...this.searchOptions.filters.filter(filter => !fields.includes(filter.field)),
              ...this.defaultFilters
            ]
          };
        }
      },
      async mounted() {
        if (window.location.hash === '#add') {
          this.$refs.addClientModal.openModal();
        }

        // Fiche demandée par son nom : seul ce filtre s'applique, quel que soit le statut du client
        if (window.location.hash === '#edit-client') {
          const filters = CustomTableService.data.get(this.$refs.table.tableId).currentFilters;
          filters.splice(0, filters.length, { field: "name", operator: FILTER_OPERATOR_ENUM.EQUAL.value, variable: this.$route.query.name });
        }

        await this.loadData();

        if(window.location.hash === "#edit-client"){
          this.$refs.editClientModal.openModal(this.data[0])
        }

        // Fiche demandée par son identifiant (ex : depuis un document dont le client est incomplet), ouverte sur la section voulue
        if(window.location.hash === "#open-client"){
          const client = await ClientController.get(this.$route.query.clientId);
          if (!client.errors) {
            this.$refs.editClientModal.openModal(client, this.$route.query.section);
          }
        }
      },
      methods: {
        openClient(client) {
          this.$refs.editClientModal.openModal(client);
        },
        // Bascule entre les clients de la rubrique et les clients archivés (quel que soit leur statut)
        async setArchiveView(isArchiveView) {
          if (this.isArchiveView === isArchiveView) return;
          const filters = CustomTableService.data.get(this.$refs.table.tableId).currentFilters;
          const isSameFilter = (a, b) => a.field === b.field && a.operator === b.operator && JSON.stringify(a.variable) === JSON.stringify(b.variable);
          const tabFilters = this.defaultFilters || [];
          const toRemove = isArchiveView ? [...tabFilters, ACTIVE_STATE_FILTER] : [ARCHIVED_STATE_FILTER];
          const toAdd = isArchiveView ? [ARCHIVED_STATE_FILTER] : [ACTIVE_STATE_FILTER, ...tabFilters];
          for (let i = filters.length - 1; i >= 0; i--) {
            if (toRemove.some(filter => isSameFilter(filters[i], filter))) filters.splice(i, 1);
          }
          filters.push(...toAdd.map(filter => ({...filter})));
          this.isArchiveView = isArchiveView;
          await this.loadData();
        },
        goToBusiness(client) {
          router.push(`/businesses?clientId=${client.id}#add-quote`);
        },
        timeAgo,
        async loadData() {
          this.$refs.table?.startLoading();
          this.data = [];
          const resp = await ClientController.getAll(this.$refs.table.buildURLParams());
          if (!resp.errors) {
            for (let i = 0; i < resp.length; i++) {
              // Decrypt needed for some fields
              let client = {};
              client.name = resp[i].name;
              client.description = resp[i].description;
              client.phone = resp[i].phone;
              client.mail = resp[i].mail;
              client.contact = resp[i].contact;
              client.lastContact = resp[i].lastContact;
              this.data.push(Object.assign(resp[i], client));
            }
          }
          this.$refs.table?.stopLoading();
          if (this.loadedCallback) this.loadedCallback();
        }
      }
    }
</script>

<style lang="stylus" scoped>
  @require '~/src/common/style/common.styl';
  @require '~/src/common/style/businessTable.styl';

  .button-icon
    margin-right 10px

  .name
    display flex
    align-items center
    gap 10px
    // Le nom est tronqué par « … » quand il est trop long (la cellule porte l'infobulle)
    p
      min-width 0
      overflow hidden
      text-overflow ellipsis
    .fa-circle-info
      flex 0 0 auto
      height 20px
      width 20px
      color #499dbd
</style>
